/**
 * AICreditUsagePanel — admin-only insight into AI credit consumption (iter48+).
 * Shows: summary tiles + top-N users by usage + per-source split for the current month.
 */
import React, { useEffect, useState } from 'react';
import {
  Sparkles, AlertTriangle, RefreshCw, Loader2, TrendingUp,
  Users as UsersIcon, FileText, ShieldCheck,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const SummaryTile = ({ icon: Icon, label, value, accent, sublabel, testId }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
          <p
            className={`text-2xl font-bold ${accent || 'text-slate-100'} mt-1`}
            data-testid={testId}
          >
            {value}
          </p>
          {sublabel && <p className="text-[10px] text-slate-500 mt-0.5">{sublabel}</p>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg bg-[#1A2347] ${accent || 'text-slate-300'}`}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const UsageRow = ({ row }) => {
  const pct = row.unlimited ? 0 : Math.min(100, row.usage_pct);
  const tone = row.over_cap
    ? 'bg-rose-500'
    : pct >= 80
      ? 'bg-amber-400'
      : 'bg-emerald-500';
  return (
    <tr className="border-b border-[#1F2A4D] hover:bg-[#1A2347]/40" data-testid={`credit-top-user-${row.id}`}>
      <td className="py-2.5 px-3">
        <div className="text-sm text-slate-100 truncate max-w-[260px]" title={row.email}>{row.email}</div>
        <div className="text-[10px] text-slate-500 truncate">{row.full_name || '—'}</div>
      </td>
      <td className="py-2.5 px-3 text-xs text-slate-300">
        <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400">
          {row.subscription_plan || 'free'}
        </Badge>
      </td>
      <td className="py-2.5 px-3 text-right text-sm font-semibold text-slate-100" data-testid={`credit-used-${row.id}`}>
        {row.used}
      </td>
      <td className="py-2.5 px-3 text-right text-xs text-slate-400">
        {row.unlimited ? '∞' : row.ai_credit_cap}
      </td>
      <td className="py-2.5 px-3 w-[120px]">
        {row.unlimited ? (
          <span className="text-[10px] text-emerald-400">unlimited</span>
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-[#1F2A4D] rounded-full overflow-hidden">
              <div
                className={`h-full ${tone} transition-all`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[10px] text-slate-400 w-9 text-right">
              {Math.round(row.usage_pct)}%
            </span>
          </div>
        )}
      </td>
      <td className="py-2.5 px-3 text-right">
        {row.over_cap && (
          <Badge className="text-[10px] bg-rose-900/40 text-rose-300 border-rose-800/40">
            OVER CAP
          </Badge>
        )}
        {!row.over_cap && row.usage_pct >= 80 && (
          <Badge className="text-[10px] bg-amber-900/40 text-amber-300 border-amber-800/40">
            NEAR CAP
          </Badge>
        )}
      </td>
    </tr>
  );
};

const AICreditUsagePanel = () => {
  const [summary, setSummary] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [bySource, setBySource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const [s, top, src] = await Promise.all([
        api.get('/admin/ai-credits/summary'),
        api.get('/admin/ai-credits/top-users?limit=15'),
        api.get('/admin/ai-credits/by-source'),
      ]);
      setSummary(s.data);
      setTopUsers(top.data.items);
      setBySource(src.data.items);
    } catch (e) {
      toast.error('Failed to load AI credit metrics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  return (
    <div className="p-6 max-w-[1700px] mx-auto" data-testid="admin-ai-credit-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 inline-flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-rose-400" /> AI Credit Usage
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Auto-run AI consumption across all users for {summary?.period || '—'}.
            Spot accounts approaching their caps before they hit overage.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchAll({ silent: true })}
          disabled={refreshing}
          className="border-[#2A3658] bg-transparent text-slate-200"
          data-testid="admin-ai-credit-refresh"
        >
          {refreshing
            ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {loading || !summary ? (
          [1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 bg-[#0F1730]/60" />)
        ) : (
          <>
            <SummaryTile
              icon={TrendingUp}
              label="Total credits used"
              value={summary.total_used_this_month.toLocaleString()}
              accent="text-rose-300"
              sublabel={`${summary.user_count} non-admin users`}
              testId="credit-summary-total-used"
            />
            <SummaryTile
              icon={UsersIcon}
              label="Over cap"
              value={summary.over_cap_users}
              accent={summary.over_cap_users > 0 ? 'text-rose-400' : 'text-emerald-400'}
              sublabel={summary.over_cap_users > 0 ? 'review for overage fees' : 'all within budget'}
              testId="credit-summary-over-cap"
            />
            <SummaryTile
              icon={FileText}
              label="Ledger entries"
              value={summary.ledger_entries_this_month.toLocaleString()}
              sublabel={`${summary.ledger_entries_all_time.toLocaleString()} all-time`}
              testId="credit-summary-ledger-month"
            />
            <SummaryTile
              icon={ShieldCheck}
              label="Period"
              value={summary.period}
              accent="text-slate-200"
              sublabel="rolls on the 1st"
              testId="credit-summary-period"
            />
          </>
        )}
      </div>

      {/* By-source split */}
      {!loading && bySource.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-slate-100">Usage by call type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {bySource.map((s) => (
                <div
                  key={s.source}
                  className="rounded-lg border border-[#1F2A4D] bg-[#0A1024]/40 p-3"
                  data-testid={`credit-by-source-${s.source}`}
                >
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">{s.source.replace(/_/g, ' ')}</p>
                  <p className="text-xl font-bold text-rose-300 mt-1">{s.count.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top users table */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm text-slate-100">Top users by usage</CardTitle>
          <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-300">
            {topUsers.length} accounts
          </Badge>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 bg-[#1A2347]/60" />)}
            </div>
          ) : topUsers.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm" data-testid="credit-top-users-empty">
              <Sparkles className="h-8 w-8 text-slate-600 mx-auto mb-2" />
              No usage recorded for this month yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-[#1F2A4D]">
                    <th className="py-2 px-3">User</th>
                    <th className="py-2 px-3">Plan</th>
                    <th className="py-2 px-3 text-right">Used</th>
                    <th className="py-2 px-3 text-right">Cap</th>
                    <th className="py-2 px-3">Progress</th>
                    <th className="py-2 px-3 text-right">Status</th>
                  </tr>
                </thead>
                <tbody data-testid="credit-top-users-table">
                  {topUsers.map((row) => <UsageRow key={row.id} row={row} />)}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-4 text-xs text-slate-500 flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
        <span>
          Counts include only AUTO-RUN AI calls (Credit Saver feature) — manual Generate clicks
          and cached re-fetches don&apos;t consume credits. Admin accounts are exempt and excluded
          from this view. Counter resets on the 1st of each month.
        </span>
      </div>
    </div>
  );
};

export default AICreditUsagePanel;
